import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import {
  GanttParameterVO,
  SerializedGanttParameter,
} from '../../../../domain/value-object/GanttParameterVO'
import { DISPLAY_DATE_FORMAT } from '../../../../utils/date'

export type ProgramManagementPageState = {
  selectedProgramUuid: string | undefined
  aggregateTarget: AggregateTarget
  aggregateField: AggregateField
  workloadUnit: WorkloadUnit
  rowHeight: number
  dateFormat: string
  ganttVisible: boolean
  ganttParameter: GanttParameterVO
}

export type ProgramManagementPageStateApi = ProgramManagementPageState & {
  initialized: boolean
  setSelectedProgramUuid: (value: string | undefined) => void
  setAggregateTarget: (value: AggregateTarget) => void
  setAggregateField: (value: AggregateField) => void
  setWorkloadUnit: (value: WorkloadUnit) => void
  setRowHeight: (value: number) => void
  setDateFormat: (value: string) => void
  setGanttVisible: (value: boolean) => void
  setGanttParameter: (value: GanttParameterVO) => void
}

const DEFAULT_STATE: ProgramManagementPageState = {
  selectedProgramUuid: undefined,
  aggregateTarget: WbsItemType.DELIVERABLE as AggregateTarget,
  aggregateField: AggregateField.WBS_ITEM_WORKLOAD,
  workloadUnit: WorkloadUnit.HOUR,
  rowHeight: ROW_HEIGHT.SMALL,
  dateFormat: DISPLAY_DATE_FORMAT,
  ganttVisible: false,
  ganttParameter: GanttParameterVO.defaultValue(),
}

export const usePageState = (
  functionUuid: string
): ProgramManagementPageStateApi => {
  // selectedProgramUuid はこのページの状態であり、その初期値の決定手段の一つとして
  // URL(/programManagement/:code)からの指定がある。URL指定時は永続化された選択を
  // 復元せず、URL指定を優先する（判定は下の復元処理内で行う）。
  const { code: programCodeInUrl } = useParams<{ code?: string }>()
  const [selectedProgramUuid, setSelectedProgramUuid] = useState<
    string | undefined
  >(DEFAULT_STATE.selectedProgramUuid)
  const [aggregateTarget, setAggregateTarget] = useState<AggregateTarget>(
    DEFAULT_STATE.aggregateTarget
  )
  const [aggregateField, setAggregateField] = useState<AggregateField>(
    DEFAULT_STATE.aggregateField
  )
  const [workloadUnit, setWorkloadUnit] = useState<WorkloadUnit>(
    DEFAULT_STATE.workloadUnit
  )
  const [rowHeight, setRowHeight] = useState<number>(DEFAULT_STATE.rowHeight)
  const [dateFormat, setDateFormat] = useState<string>(DEFAULT_STATE.dateFormat)
  const [ganttVisible, setGanttVisible] = useState<boolean>(
    DEFAULT_STATE.ganttVisible
  )
  const [ganttParameter, setGanttParameter] = useState<GanttParameterVO>(
    DEFAULT_STATE.ganttParameter
  )
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    const restore = async () => {
      try {
        const response = await uiStates.get({
          applicationFunctionUuid: functionUuid,
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
        })
        const stored = response.json
        if (stored?.value) {
          const payload: Partial<ProgramManagementPageState> = JSON.parse(
            stored.value
          )
          // URLでプログラムが指定されている初回表示では、必ずURL指定のプログラムを表示する。
          // 復元値でここを上書きすると、URLマッチ(useProgramManagementData)との非同期の
          // 後勝ちで別プログラムが選択されるレース条件になるため、URL指定時は復元しない。
          // （復元をスキップしても、ヘッダーのセレクトで選び直した値は保存・以後反映される）
          if (payload.selectedProgramUuid !== undefined && !programCodeInUrl) {
            setSelectedProgramUuid(payload.selectedProgramUuid)
          }
          if (payload.aggregateTarget !== undefined) {
            setAggregateTarget(payload.aggregateTarget)
          }
          if (payload.aggregateField !== undefined) {
            setAggregateField(payload.aggregateField)
          }
          if (payload.workloadUnit !== undefined) {
            setWorkloadUnit(payload.workloadUnit)
          }
          if (payload.rowHeight !== undefined) {
            setRowHeight(payload.rowHeight)
          }
          if (payload.dateFormat !== undefined) {
            setDateFormat(payload.dateFormat)
          }
          if (payload.ganttVisible !== undefined) {
            setGanttVisible(payload.ganttVisible)
          }
          if (payload.ganttParameter !== undefined) {
            setGanttParameter(
              GanttParameterVO.deserialize(
                payload.ganttParameter as unknown as SerializedGanttParameter
              )
            )
          }
        }
      } finally {
        setInitialized(true)
      }
    }
    restore()
  }, [functionUuid, programCodeInUrl])

  useEffect(() => {
    if (!initialized) return
    uiStates.update(
      {
        key: UiStateKey.PageState,
        scope: UiStateScope.User,
        value: JSON.stringify({
          selectedProgramUuid,
          aggregateTarget,
          aggregateField,
          workloadUnit,
          rowHeight,
          dateFormat,
          ganttVisible,
          ganttParameter: ganttParameter.serialize(),
        }),
      },
      functionUuid
    )
  }, [
    initialized,
    selectedProgramUuid,
    aggregateTarget,
    aggregateField,
    workloadUnit,
    rowHeight,
    dateFormat,
    ganttVisible,
    ganttParameter,
    functionUuid,
  ])

  const updateSelectedProgramUuid = useCallback((value: string | undefined) => {
    setSelectedProgramUuid(value)
  }, [])

  const updateAggregateTarget = useCallback((value: AggregateTarget) => {
    setAggregateTarget(value)
  }, [])

  const updateAggregateField = useCallback((value: AggregateField) => {
    setAggregateField(value)
  }, [])

  const updateWorkloadUnit = useCallback((value: WorkloadUnit) => {
    setWorkloadUnit(value)
  }, [])

  const updateRowHeight = useCallback((value: number) => {
    setRowHeight(value)
  }, [])

  const updateDateFormat = useCallback((value: string) => {
    setDateFormat(value)
  }, [])

  const updateGanttVisible = useCallback((value: boolean) => {
    setGanttVisible(value)
  }, [])

  const updateGanttParameter = useCallback((value: GanttParameterVO) => {
    setGanttParameter(value)
  }, [])

  return useMemo(
    () => ({
      initialized,
      selectedProgramUuid,
      aggregateTarget,
      aggregateField,
      workloadUnit,
      rowHeight,
      dateFormat,
      ganttVisible,
      ganttParameter,
      setSelectedProgramUuid: updateSelectedProgramUuid,
      setAggregateTarget: updateAggregateTarget,
      setAggregateField: updateAggregateField,
      setWorkloadUnit: updateWorkloadUnit,
      setRowHeight: updateRowHeight,
      setDateFormat: updateDateFormat,
      setGanttVisible: updateGanttVisible,
      setGanttParameter: updateGanttParameter,
    }),
    [
      initialized,
      selectedProgramUuid,
      aggregateTarget,
      aggregateField,
      workloadUnit,
      rowHeight,
      dateFormat,
      ganttVisible,
      ganttParameter,
      updateSelectedProgramUuid,
      updateAggregateTarget,
      updateAggregateField,
      updateWorkloadUnit,
      updateRowHeight,
      updateDateFormat,
      updateGanttVisible,
      updateGanttParameter,
    ]
  )
}
